/**
 * manual-quote-dialogs.tsx — Hộp thoại "Xác nhận đã thanh toán" (tạo tổ chức nếu khách mới + tạo đơn) và "Huỷ báo giá".
 */
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox, Textarea } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { platformApiError } from '@/lib/platform-client'
import { useConfirmPaid, useQuoteAction } from '@/hooks/use-manual-quotes'
import { fmt, isoDate, newIdempotencyKey, type ManualQuote } from './manual-quote-helpers'

export function ConfirmPaidDialog({ quote, onClose, onDone }: { quote: ManualQuote; onClose: () => void; onDone?: (q: ManualQuote) => void }) {
  // Một mã chống gửi trùng cho mỗi lần mở hộp thoại (bấm lại / mạng chập chờn → server trả kết quả cũ).
  const idempotencyKey = useMemo(() => newIdempotencyKey(), [])
  const confirm = useConfirmPaid()
  const isNew = !quote.orgId
  const c = quote.customer
  const [paymentRef, setPaymentRef] = useState('')
  const [paidDate, setPaidDate] = useState(isoDate(new Date()))
  const [reason, setReason] = useState(`Khách chuyển khoản theo báo giá ${quote.number}`)
  const [org, setOrg] = useState({ orgName: c.name, ownerFullName: c.contact || c.representative, ownerEmail: c.email, ownerPassword: '', referralCode: '' })
  const [withInvoice, setWithInvoice] = useState(!!c.taxCode)
  const [inv, setInv] = useState({
    buyerType: c.taxCode ? 'company' : 'individual', companyName: c.name, taxCode: c.taxCode, buyerName: c.representative || c.contact,
    address: c.address, emails: c.email, phone: c.phone,
  })
  const [error, setError] = useState<string | null>(null)

  const submit = () => {
    setError(null)
    confirm.mutate({
      id: quote.id,
      body: {
        idempotencyKey, paymentRef, paidDate, reason,
        newOrg: isNew ? org : null,
        invoice: withInvoice
          ? {
              buyerType: inv.buyerType, companyName: inv.buyerType === 'company' ? inv.companyName : null, taxCode: inv.taxCode || null,
              buyerName: inv.buyerName || null, address: inv.address, emails: inv.emails.split(/[,;\s]+/).filter(Boolean), phone: inv.phone || null,
            }
          : null,
      },
    }, {
      onSuccess: (r) => {
        toast.success(r.replay ? 'Báo giá đã được xác nhận trước đó' : `Đã tạo đơn${r.orgCreated ? ' và tổ chức mới' : ''} — gói đã được áp`)
        if (r.referralError) toast.warning(`Chưa gắn được mã giới thiệu: ${r.referralError}`)
        for (const w of r.warnings ?? []) toast.warning(w)
        onDone?.(r.quote)
        onClose()
      },
      onError: (err) => setError(platformApiError(err)),
    })
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Xác nhận đã thanh toán — {quote.number}</DialogTitle>
          <DialogDescription>
            Tạo đơn {fmt(quote.total)} ₫ (chuyển khoản) và áp gói ngay như thanh toán trực tuyến. Mỗi báo giá chỉ tạo một đơn; giá lấy theo báo giá đã lập.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="mq-ref">Mã giao dịch / tham chiếu</Label>
            <Input id="mq-ref" value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} placeholder="VD: FT26273xxxxx" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="mq-date">Ngày khách chuyển khoản</Label>
            <Input id="mq-date" type="date" value={paidDate} max={isoDate(new Date())} onChange={(e) => setPaidDate(e.target.value)} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="mq-reason">Lý do / ghi chú (ghi nhật ký)</Label>
            <Input id="mq-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
        </div>

        {isNew && (
          <div className="mt-2 space-y-2 rounded-lg border p-3">
            <p className="text-sm font-medium">Khách mới — tạo tổ chức + tài khoản chủ sở hữu</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1 sm:col-span-2"><Label>Tên tổ chức</Label><Input value={org.orgName} onChange={(e) => setOrg({ ...org, orgName: e.target.value })} /></div>
              <div className="space-y-1"><Label>Họ tên chủ sở hữu</Label><Input value={org.ownerFullName} onChange={(e) => setOrg({ ...org, ownerFullName: e.target.value })} /></div>
              <div className="space-y-1"><Label>Email đăng nhập</Label><Input type="email" value={org.ownerEmail} onChange={(e) => setOrg({ ...org, ownerEmail: e.target.value })} /></div>
              <div className="space-y-1"><Label>Mật khẩu tạm (≥ 8 ký tự)</Label><Input type="password" autoComplete="new-password" value={org.ownerPassword} onChange={(e) => setOrg({ ...org, ownerPassword: e.target.value })} /></div>
              <div className="space-y-1"><Label>Mã người giới thiệu (tuỳ chọn)</Label><Input value={org.referralCode} onChange={(e) => setOrg({ ...org, referralCode: e.target.value.toUpperCase() })} /></div>
            </div>
          </div>
        )}

        <div className="mt-2 space-y-2 rounded-lg border p-3">
          <div className="flex items-center gap-2">
            <Checkbox id="mq-inv" checked={withInvoice} onCheckedChange={(v) => setWithInvoice(v === true)} disabled={quote.total <= 0} />
            <Label htmlFor="mq-inv" className="font-normal">Xuất hoá đơn điện tử cho đơn này</Label>
          </div>
          {withInvoice && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1 sm:col-span-2">
                <Label>Loại người mua</Label>
                <div className="flex gap-4 text-sm">
                  {(['company', 'individual'] as const).map((t) => (
                    <label key={t} className="flex items-center gap-1.5">
                      <input type="radio" name="mq-buyer" checked={inv.buyerType === t} onChange={() => setInv({ ...inv, buyerType: t })} />
                      {t === 'company' ? 'Công ty' : 'Cá nhân / hộ kinh doanh'}
                    </label>
                  ))}
                </div>
              </div>
              {inv.buyerType === 'company' && (
                <>
                  <div className="space-y-1"><Label>Tên đơn vị</Label><Input value={inv.companyName} onChange={(e) => setInv({ ...inv, companyName: e.target.value })} /></div>
                  <div className="space-y-1"><Label>Mã số thuế</Label><Input value={inv.taxCode} onChange={(e) => setInv({ ...inv, taxCode: e.target.value })} /></div>
                </>
              )}
              <div className="space-y-1"><Label>{inv.buyerType === 'company' ? 'Người mua hàng' : 'Họ tên'}</Label><Input value={inv.buyerName} onChange={(e) => setInv({ ...inv, buyerName: e.target.value })} /></div>
              <div className="space-y-1"><Label>Điện thoại</Label><Input value={inv.phone} onChange={(e) => setInv({ ...inv, phone: e.target.value })} /></div>
              <div className="space-y-1 sm:col-span-2"><Label>Địa chỉ</Label><Input value={inv.address} onChange={(e) => setInv({ ...inv, address: e.target.value })} /></div>
              <div className="space-y-1 sm:col-span-2"><Label>Email nhận hoá đơn (cách nhau dấu phẩy)</Label><Input value={inv.emails} onChange={(e) => setInv({ ...inv, emails: e.target.value })} /></div>
            </div>
          )}
        </div>

        {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Đóng</Button>
          <Button onClick={submit} disabled={confirm.isPending}>
            {confirm.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Xác nhận & tạo đơn
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function CancelQuoteDialog({ quote, onClose }: { quote: { id: string; number: string }; onClose: () => void }) {
  const action = useQuoteAction()
  const [reason, setReason] = useState('')
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Huỷ báo giá {quote.number}</DialogTitle>
          <DialogDescription>Báo giá đã huỷ không sửa / xác nhận thanh toán được nữa (vẫn sao chép được).</DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <Label htmlFor="mq-cancel">Lý do</Label>
          <Textarea id="mq-cancel" value={reason} onChange={(e) => setReason(e.target.value)} className="min-h-[60px]" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Đóng</Button>
          <Button
            variant="destructive"
            disabled={action.isPending}
            onClick={() => action.mutate({ id: quote.id, action: 'cancel', body: { reason } }, {
              onSuccess: () => { toast.success('Đã huỷ báo giá'); onClose() },
              onError: (err) => toast.error(platformApiError(err)),
            })}
          >
            Huỷ báo giá
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
